import { MOTION_PRESETS, presetTracks, type MotionPresetId } from '../animation/motionPresets';
import { baseValue, channelsForBox, displayObject, evaluateAnimation, trackValueAt, type ChannelValues } from '../animation/timeline';
import { createId } from '../lib/ids';
import { pruneSessions } from './animationRefs';
import { OpError } from './projectOps';
import {
  ANIMATION_FPS,
  AnimationSchema,
  MAX_ANIMATIONS,
  MAX_ANIMATIONS_PER_SCENE,
  MAX_ANIMATION_SECONDS,
  MAX_KEYFRAMES_PER_ANIMATION,
  MAX_KEYFRAMES_PER_TRACK,
  MAX_TRACKS,
  POSE_KEYS,
  channelRange,
  formatIssues,
  isPoseChannel,
  type Animation,
  type AnimationFps,
  type AnimationTrack,
  type Channel,
  type CharacterPose,
  type Easing,
  type Keyframe,
  type LoopMode,
  type Project,
  type Scene,
  type SceneObject,
} from './schemas';
import { isLocked, objectOf, sceneOf, updateObjects } from './sceneOps';

/**
 * Pure, validated operations on keyframe animations (Phase 3B). Every function returns a
 * new Project or throws OpError and changes nothing; each changed animation is validated
 * (schema + references) before it is returned. Keyframes stay sorted by frame with at
 * most one keyframe per frame on a track.
 */

export const DEFAULT_SECONDS = 4;
export const DEFAULT_FPS: AnimationFps = 24;
export const DEFAULT_EASING: Easing = 'linear';

export const CHANNEL_LABELS: Record<Channel, string> = {
  x: 'X',
  y: 'Y',
  rotation: 'Rotation',
  scaleX: 'Scale X',
  scaleY: 'Scale Y',
  opacity: 'Opacity',
  visible: 'Visible',
  'pose.head': 'Head',
  'pose.torso': 'Torso',
  'pose.armL': 'Left arm',
  'pose.armR': 'Right arm',
  'pose.legL': 'Left leg',
  'pose.legR': 'Right leg',
  'pose.headX': 'Head X',
  'pose.headY': 'Head Y',
  'pose.torsoX': 'Torso X',
  'pose.torsoY': 'Torso Y',
};

const round = (v: number, digits: number) => Math.round(v * 10 ** digits) / 10 ** digits;

/** Clamps and rounds a value for a channel; non-finite values are refused. */
export function cleanValue(channel: Channel, value: number): number {
  if (!Number.isFinite(value)) throw new OpError(`${CHANNEL_LABELS[channel]} must be a number.`);
  const [lo, hi] = channelRange(channel);
  const v = Math.min(hi, Math.max(lo, value));
  if (channel === 'visible') return v >= 0.5 ? 1 : 0;
  if (channel === 'opacity' || channel === 'scaleX' || channel === 'scaleY') return round(v, 4);
  if (channel.startsWith('pose.') && channelRange(channel)[1] < 1) return round(v, 4);
  return round(v, 2);
}

// ---------------------------------------------------------------- Lookup and validation

export function animationOf(project: Project, animationId: string): Animation {
  const a = project.animations.find((x) => x.id === animationId);
  if (!a) throw new OpError('That animation no longer exists.');
  return a;
}

export const animationsOf = (project: Project, sceneId: string): Animation[] => project.animations.filter((a) => a.sceneId === sceneId);

/** Checks an animation against its scene (objects exist; pose channels only on characters). */
function checkReferences(scene: Scene, a: Animation): void {
  for (const t of a.tracks) {
    const o = scene.objects.find((x) => x.id === t.objectId);
    if (!o) throw new OpError('That object is no longer in the scene.');
    if (isPoseChannel(t.channel) && o.type !== 'character') throw new OpError(`Pose channels only apply to characters (“${o.name}” is a ${o.type}).`);
  }
}

/** Validates a changed (or new) animation; returns the project with it replaced (or added). */
export function withAnimation(project: Project, a: Animation, isNew = false): Project {
  const parsed = AnimationSchema.safeParse(a);
  if (!parsed.success) throw new OpError(`That change would make the animation invalid: ${formatIssues(parsed.error)[0] ?? 'unknown problem'}`);
  checkReferences(sceneOf(project, a.sceneId), a);
  if (isNew) {
    if (project.animations.length >= MAX_ANIMATIONS) throw new OpError(`A project can hold at most ${MAX_ANIMATIONS} animations.`);
    if (animationsOf(project, a.sceneId).length >= MAX_ANIMATIONS_PER_SCENE) throw new OpError(`A scene can have at most ${MAX_ANIMATIONS_PER_SCENE} animations.`);
    if (project.animations.some((x) => x.id === a.id)) throw new OpError('An animation with that id already exists.');
    return { ...project, animations: [...project.animations, a] };
  }
  animationOf(project, a.id);
  return { ...project, animations: project.animations.map((x) => (x.id === a.id ? a : x)) };
}

function trackOf(a: Animation, trackId: string): AnimationTrack {
  const t = a.tracks.find((x) => x.id === trackId);
  if (!t) throw new OpError('That track no longer exists.');
  return t;
}

export const findTrack = (a: Animation, objectId: string, channel: Channel): AnimationTrack | undefined =>
  a.tracks.find((t) => t.objectId === objectId && t.channel === channel);

function editableObject(project: Project, a: Animation, objectId: string): SceneObject {
  const scene = sceneOf(project, a.sceneId);
  const o = objectOf(scene, objectId);
  if (isLocked(scene, o)) throw new OpError(`“${o.name}” is locked. Unlock it to animate it.`);
  return o;
}

const checkFrame = (a: Animation, frame: number) => {
  if (!Number.isInteger(frame) || frame < 0 || frame >= a.durationFrames) {
    throw new OpError(`Frame ${frame} is outside the animation (0–${a.durationFrames - 1}).`);
  }
};

// ---------------------------------------------------------------- Animations

export interface AnimationOptions {
  name?: string;
  seconds?: number;
  fps?: number;
  loop?: LoopMode;
}

export function checkFps(fps: number): AnimationFps {
  if (!(ANIMATION_FPS as readonly number[]).includes(fps)) throw new OpError(`Frame rate must be one of ${ANIMATION_FPS.join(', ')} fps.`);
  return fps as AnimationFps;
}

export function framesFor(seconds: number, fps: number): number {
  if (!Number.isFinite(seconds) || seconds <= 0) throw new OpError('Duration must be a positive number of seconds.');
  if (seconds > MAX_ANIMATION_SECONDS) throw new OpError(`An animation can last at most ${MAX_ANIMATION_SECONDS} seconds.`);
  return Math.max(1, Math.round(seconds * fps));
}

export function createAnimation(project: Project, sceneId: string, options: AnimationOptions = {}, id = createId('anm')): { project: Project; id: string } {
  const scene = sceneOf(project, sceneId);
  const fps = checkFps(options.fps ?? DEFAULT_FPS);
  const taken = new Set(animationsOf(project, sceneId).map((a) => a.name));
  let name = options.name?.trim().slice(0, 60) || 'Animation 1';
  if (!options.name?.trim()) for (let n = 1; taken.has(name); n++) name = `Animation ${n + 1}`;
  const animation: Animation = { id, name, sceneId: scene.id, fps, durationFrames: framesFor(options.seconds ?? DEFAULT_SECONDS, fps), loop: options.loop ?? 'loop', tracks: [] };
  return { project: withAnimation(project, animation, true), id };
}

export function renameAnimation(project: Project, animationId: string, name: string): Project {
  const trimmed = name.trim().slice(0, 60);
  if (!trimmed) throw new OpError('An animation name cannot be empty.');
  return withAnimation(project, { ...animationOf(project, animationId), name: trimmed });
}

/** Copies an animation (same scene and objects) with fresh animation and track ids. */
export function duplicateAnimation(project: Project, animationId: string, id = createId('anm')): { project: Project; id: string } {
  const a = animationOf(project, animationId);
  const copy: Animation = { ...structuredClone(a), id, name: `${a.name} copy`.slice(0, 60), tracks: a.tracks.map((t) => ({ ...structuredClone(t), id: createId('trk') })) };
  return { project: withAnimation(project, copy, true), id };
}

export function deleteAnimation(project: Project, animationId: string): Project {
  animationOf(project, animationId);
  // Sessions playing it are unlinked (they keep their reactions on the base scene).
  return pruneSessions({ ...project, animations: project.animations.filter((a) => a.id !== animationId) });
}

/**
 * Changes frame rate, duration and/or loop mode.
 * - A new frame rate keeps every keyframe at the same *time* (frames are rescaled and
 *   rounded); if two keyframes of a track would land on the same frame the change is
 *   refused, naming them.
 * - A shorter duration is refused while keyframes lie past the new end, unless
 *   `dropOutside` is set (those keyframes are then deleted).
 */
export function setAnimationTiming(
  project: Project,
  animationId: string,
  patch: { fps?: number; seconds?: number; durationFrames?: number; loop?: LoopMode },
  options: { dropOutside?: boolean } = {},
): Project {
  const a = animationOf(project, animationId);
  const fps = checkFps(patch.fps ?? a.fps);
  let tracks = a.tracks;
  let duration = a.durationFrames;
  if (fps !== a.fps) {
    const ratio = fps / a.fps;
    duration = Math.max(1, Math.round(a.durationFrames * ratio));
    tracks = a.tracks.map((t) => {
      const keyframes = t.keyframes.map((k) => ({ ...k, frame: Math.min(duration - 1, Math.round(k.frame * ratio)) }));
      keyframes.forEach((k, i) => {
        if (i > 0 && keyframes[i - 1]!.frame === k.frame) {
          throw new OpError(`At ${fps} fps two ${CHANNEL_LABELS[t.channel]} keyframes (frames ${t.keyframes[i - 1]!.frame} and ${t.keyframes[i]!.frame}) would land on the same frame. Move or delete one first.`);
        }
      });
      return { ...t, keyframes };
    });
  }
  if (patch.seconds !== undefined) duration = framesFor(patch.seconds, fps);
  if (patch.durationFrames !== undefined) {
    if (!Number.isInteger(patch.durationFrames) || patch.durationFrames < 1) throw new OpError('Duration must be at least one frame.');
    duration = patch.durationFrames;
  }
  if (duration > MAX_ANIMATION_SECONDS * fps) throw new OpError(`An animation can last at most ${MAX_ANIMATION_SECONDS} seconds.`);
  const outside = tracks.reduce((n, t) => n + t.keyframes.filter((k) => k.frame >= duration).length, 0);
  if (outside > 0) {
    if (!options.dropOutside) throw new OpError(`${outside} keyframe${outside === 1 ? ' lies' : 's lie'} after the new end (frame ${duration - 1}). Delete them first, or confirm to remove them.`);
    tracks = tracks.map((t) => ({ ...t, keyframes: t.keyframes.filter((k) => k.frame < duration) }));
  }
  return withAnimation(project, { ...a, fps, durationFrames: duration, loop: patch.loop ?? a.loop, tracks });
}

/** Keyframes that would be removed by shortening an animation to `durationFrames`. */
export const keyframesAfter = (a: Animation, durationFrames: number): number => a.tracks.reduce((n, t) => n + t.keyframes.filter((k) => k.frame >= durationFrames).length, 0);

// ---------------------------------------------------------------- Tracks

export function addTrack(project: Project, animationId: string, objectId: string, channel: Channel, id = createId('trk')): { project: Project; trackId: string } {
  const a = animationOf(project, animationId);
  editableObject(project, a, objectId);
  if (findTrack(a, objectId, channel)) throw new OpError(`${CHANNEL_LABELS[channel]} is already animated for that object.`);
  if (a.tracks.length >= MAX_TRACKS) throw new OpError(`An animation can have at most ${MAX_TRACKS} tracks.`);
  return { project: withAnimation(project, { ...a, tracks: [...a.tracks, { id, objectId, channel, keyframes: [] }] }), trackId: id };
}

export function removeTrack(project: Project, animationId: string, trackId: string): Project {
  const a = animationOf(project, animationId);
  trackOf(a, trackId);
  return withAnimation(project, { ...a, tracks: a.tracks.filter((t) => t.id !== trackId) });
}

/** Removes every track of an object in an animation. */
export function removeObjectTracks(project: Project, animationId: string, objectId: string): Project {
  const a = animationOf(project, animationId);
  return withAnimation(project, { ...a, tracks: a.tracks.filter((t) => t.objectId !== objectId) });
}

// ---------------------------------------------------------------- Keyframes

export interface KeyRef {
  trackId: string;
  frame: number;
}

const upsert = (keys: readonly Keyframe[], key: Keyframe): Keyframe[] => {
  const i = keys.findIndex((k) => k.frame === key.frame);
  if (i >= 0) return keys.map((k, j) => (j === i ? key : k));
  return [...keys, key].sort((p, q) => p.frame - q.frame);
};

function checkKeyCount(a: Animation): void {
  const total = a.tracks.reduce((n, t) => n + t.keyframes.length, 0);
  if (total > MAX_KEYFRAMES_PER_ANIMATION) throw new OpError(`An animation can hold at most ${MAX_KEYFRAMES_PER_ANIMATION} keyframes.`);
  if (a.tracks.some((t) => t.keyframes.length > MAX_KEYFRAMES_PER_TRACK)) throw new OpError(`A track can hold at most ${MAX_KEYFRAMES_PER_TRACK} keyframes.`);
}

/**
 * Creates or updates the keyframe of an object's channel at a frame. The track is
 * created when missing. Repeated calls for the same frame update the same keyframe.
 */
export function setKeyframe(project: Project, animationId: string, objectId: string, channel: Channel, frame: number, value: number, easing?: Easing): Project {
  const a = animationOf(project, animationId);
  const o = editableObject(project, a, objectId);
  if (isPoseChannel(channel) && o.type !== 'character') throw new OpError(`Pose channels only apply to characters (“${o.name}” is a ${o.type}).`);
  checkFrame(a, frame);
  const v = cleanValue(channel, value);
  const existing = findTrack(a, objectId, channel);
  const previous = existing?.keyframes.find((k) => k.frame === frame);
  const key: Keyframe = { frame, value: v, easing: channel === 'visible' ? 'step' : (easing ?? previous?.easing ?? DEFAULT_EASING) };
  let tracks: AnimationTrack[];
  if (existing) tracks = a.tracks.map((t) => (t === existing ? { ...t, keyframes: upsert(t.keyframes, key) } : t));
  else {
    if (a.tracks.length >= MAX_TRACKS) throw new OpError(`An animation can have at most ${MAX_TRACKS} tracks.`);
    tracks = [...a.tracks, { id: createId('trk'), objectId, channel, keyframes: [key] }];
  }
  const next = { ...a, tracks };
  checkKeyCount(next);
  return withAnimation(project, next);
}

/** Sets several channels of one object at a frame as one change. */
export function setKeyframes(project: Project, animationId: string, objectId: string, frame: number, values: ChannelValues, easing?: Easing): Project {
  let p = project;
  for (const [channel, value] of Object.entries(values) as [Channel, number][]) p = setKeyframe(p, animationId, objectId, channel, frame, value, easing);
  return p;
}

export function deleteKeyframes(project: Project, animationId: string, refs: readonly KeyRef[]): Project {
  const a = animationOf(project, animationId);
  if (refs.length === 0) throw new OpError('No keyframe is selected.');
  const doomed = new Set(refs.map((r) => `${r.trackId}@${r.frame}`));
  for (const r of refs) if (!trackOf(a, r.trackId).keyframes.some((k) => k.frame === r.frame)) throw new OpError('That keyframe no longer exists.');
  return withAnimation(project, { ...a, tracks: a.tracks.map((t) => ({ ...t, keyframes: t.keyframes.filter((k) => !doomed.has(`${t.id}@${k.frame}`)) })) });
}

/**
 * Moves keyframes by whole frames. Refused when any would leave the animation or land on
 * a frame already holding a keyframe that is not moving with them.
 */
export function moveKeyframes(project: Project, animationId: string, refs: readonly KeyRef[], delta: number): Project {
  const a = animationOf(project, animationId);
  if (!Number.isInteger(delta)) throw new OpError('Keyframes move by whole frames.');
  if (refs.length === 0) throw new OpError('No keyframe is selected.');
  if (delta === 0) return project;
  const moving = new Set(refs.map((r) => `${r.trackId}@${r.frame}`));
  const tracks = a.tracks.map((t) => {
    const mine = t.keyframes.filter((k) => moving.has(`${t.id}@${k.frame}`));
    if (mine.length === 0) return t;
    const staying = t.keyframes.filter((k) => !moving.has(`${t.id}@${k.frame}`));
    const moved = mine.map((k) => ({ ...k, frame: k.frame + delta }));
    for (const k of moved) {
      if (k.frame < 0 || k.frame >= a.durationFrames) throw new OpError(`That would move a keyframe outside the animation (0–${a.durationFrames - 1}).`);
      if (staying.some((s) => s.frame === k.frame)) throw new OpError(`Frame ${k.frame} already has a ${CHANNEL_LABELS[t.channel]} keyframe.`);
    }
    return { ...t, keyframes: [...staying, ...moved].sort((p, q) => p.frame - q.frame) };
  });
  for (const r of refs) if (!trackOf(a, r.trackId).keyframes.some((k) => k.frame === r.frame)) throw new OpError('That keyframe no longer exists.');
  return withAnimation(project, { ...a, tracks });
}

/** Copies keyframes so the earliest lands on `toFrame` (relative spacing kept). */
export function duplicateKeyframes(project: Project, animationId: string, refs: readonly KeyRef[], toFrame: number): { project: Project; refs: KeyRef[] } {
  const a = animationOf(project, animationId);
  if (refs.length === 0) throw new OpError('No keyframe is selected.');
  checkFrame(a, toFrame);
  const first = Math.min(...refs.map((r) => r.frame));
  const delta = toFrame - first;
  const created: KeyRef[] = [];
  const tracks = a.tracks.map((t) => {
    const source = t.keyframes.filter((k) => refs.some((r) => r.trackId === t.id && r.frame === k.frame));
    if (source.length === 0) return t;
    let keyframes = t.keyframes;
    for (const k of source) {
      const frame = k.frame + delta;
      if (frame < 0 || frame >= a.durationFrames) throw new OpError(`The copy would fall outside the animation (0–${a.durationFrames - 1}).`);
      if (keyframes.some((x) => x.frame === frame)) throw new OpError(`Frame ${frame} already has a ${CHANNEL_LABELS[t.channel]} keyframe.`);
      keyframes = upsert(keyframes, { ...k, frame });
      created.push({ trackId: t.id, frame });
    }
    return { ...t, keyframes };
  });
  const next = { ...a, tracks };
  checkKeyCount(next);
  return { project: withAnimation(project, next), refs: created };
}

/** Edits one keyframe's value, easing and/or frame. */
export function updateKeyframe(project: Project, animationId: string, ref: KeyRef, patch: { value?: number; easing?: Easing; frame?: number }): Project {
  const a = animationOf(project, animationId);
  const t = trackOf(a, ref.trackId);
  const k = t.keyframes.find((x) => x.frame === ref.frame);
  if (!k) throw new OpError('That keyframe no longer exists.');
  editableObject(project, a, t.objectId);
  let p = project;
  if (patch.value !== undefined || patch.easing !== undefined) {
    const value = patch.value !== undefined ? cleanValue(t.channel, patch.value) : k.value;
    const easing = t.channel === 'visible' ? 'step' : (patch.easing ?? k.easing);
    p = withAnimation(p, { ...a, tracks: a.tracks.map((x) => (x === t ? { ...x, keyframes: x.keyframes.map((y) => (y === k ? { ...y, value, easing } : y)) } : x)) });
  }
  if (patch.frame !== undefined && patch.frame !== ref.frame) p = moveKeyframes(p, animationId, [ref], patch.frame - ref.frame);
  return p;
}

/** Replaces an object's tracks for the given channels (used by presets). */
export function replaceTracks(project: Project, animationId: string, objectId: string, channels: Partial<Record<Channel, Keyframe[]>>): Project {
  const a = animationOf(project, animationId);
  const o = editableObject(project, a, objectId);
  const entries = Object.entries(channels) as [Channel, Keyframe[]][];
  let tracks = [...a.tracks];
  for (const [channel, keys] of entries) {
    if (isPoseChannel(channel) && o.type !== 'character') throw new OpError(`Pose channels only apply to characters (“${o.name}” is a ${o.type}).`);
    const keyframes = keys
      .filter((k) => k.frame >= 0 && k.frame < a.durationFrames)
      .map((k) => ({ ...k, value: cleanValue(channel, k.value), easing: channel === 'visible' ? ('step' as const) : k.easing }));
    const existing = tracks.findIndex((t) => t.objectId === objectId && t.channel === channel);
    if (existing >= 0) tracks[existing] = { ...tracks[existing]!, keyframes };
    else tracks = [...tracks, { id: createId('trk'), objectId, channel, keyframes }];
  }
  if (tracks.length > MAX_TRACKS) throw new OpError(`An animation can have at most ${MAX_TRACKS} tracks.`);
  const next = { ...a, tracks };
  checkKeyCount(next);
  return withAnimation(project, next);
}

/** Applies a motion preset to an object (replaces its tracks for the preset's channels). */
export function applyMotionPreset(project: Project, animationId: string, objectId: string, preset: MotionPresetId): Project {
  const a = animationOf(project, animationId);
  const o = editableObject(project, a, objectId);
  const character = o.type === 'character' ? project.characters.find((c) => c.id === o.characterId) : undefined;
  if (!MOTION_PRESETS[preset]) throw new OpError('Unknown preset.');
  return replaceTracks(project, animationId, objectId, presetTracks(preset, o, character, a));
}

// ---------------------------------------------------------------- Evaluation helpers

/** Value of an object's channel at a frame: the track's value, or the base value. */
export function valueAt(project: Project, animationId: string, objectId: string, channel: Channel, frame: number): number {
  const a = animationOf(project, animationId);
  const o = objectOf(sceneOf(project, a.sceneId), objectId);
  const t = findTrack(a, objectId, channel);
  return (t && trackValueAt(t, frame)) ?? baseValue(o, channel);
}

/** Every animated value of an object at a frame (base values are not included). */
export const valuesAt = (a: Animation, objectId: string, frame: number): ChannelValues => evaluateAnimation(a, frame).get(objectId) ?? {};

// ---------------------------------------------------------------- Auto-Key

/** A transform/pose edit expressed as the wanted *displayed* values. */
export interface TransformEdit {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  rotation?: number;
  opacity?: number;
  visible?: boolean;
  pose?: Partial<CharacterPose>;
}

/** The channels (and values) an edit changes, relative to the object as displayed. */
export function editChannels(base: SceneObject, values: ChannelValues, edit: TransformEdit): ChannelValues {
  const shown = displayObject(base, values);
  const out: ChannelValues = {};
  const moved = (edit.x !== undefined && edit.x !== shown.x) || (edit.y !== undefined && edit.y !== shown.y);
  const resized = (edit.width !== undefined && edit.width !== shown.width) || (edit.height !== undefined && edit.height !== shown.height);
  if (moved || resized) {
    const box = { x: edit.x ?? shown.x, y: edit.y ?? shown.y, width: edit.width ?? shown.width, height: edit.height ?? shown.height };
    const c = channelsForBox(base, box);
    // A move keys only the axes that changed; a resize keys position and scale together.
    if (resized || (edit.x !== undefined && edit.x !== shown.x)) out.x = c.x;
    if (resized || (edit.y !== undefined && edit.y !== shown.y)) out.y = c.y;
    if (resized) {
      out.scaleX = c.scaleX;
      out.scaleY = c.scaleY;
    }
  }
  if (edit.rotation !== undefined && edit.rotation !== shown.rotation) out.rotation = edit.rotation;
  if (edit.opacity !== undefined && edit.opacity !== shown.opacity) out.opacity = edit.opacity;
  if (edit.visible !== undefined && edit.visible !== shown.visible) out.visible = edit.visible ? 1 : 0;
  if (edit.pose && base.type === 'character') {
    const pose = shown.pose ?? base.pose;
    for (const k of POSE_KEYS) {
      const v = edit.pose[k];
      if (v !== undefined && v !== (pose?.[k] ?? 0)) out[`pose.${k}` as Channel] = v;
    }
  }
  return out;
}

/**
 * Applies a transform or pose edit with Auto-Key on.
 *
 * Every channel the edit changes is keyed at `frame` on the animation. A channel without
 * a track also gets a keyframe at frame 0 holding its value before the edit, so the
 * animation still starts where it did. Exception: at frame 0, an edit whose channels are
 * all un-animated changes the base properties instead (frame 0 *is* the base pose).
 * Returns the project and whether the edit was keyed.
 */
export function autoKeyEdit(project: Project, animationId: string, objectId: string, frame: number, edit: TransformEdit): { project: Project; keyed: boolean } {
  const a = animationOf(project, animationId);
  const o = editableObject(project, a, objectId);
  checkFrame(a, frame);
  const current = valuesAt(a, objectId, frame);
  const channels = editChannels(o, current, edit);
  const names = Object.keys(channels) as Channel[];
  if (names.length === 0) return { project, keyed: false };
  const keyed = frame > 0 || names.some((c) => findTrack(a, objectId, c));
  if (!keyed) return { project: applyBaseEdit(project, a.sceneId, o, edit), keyed: false };
  let p = project;
  for (const channel of names) {
    if (!findTrack(animationOf(p, animationId), objectId, channel) && frame > 0) {
      p = setKeyframe(p, animationId, objectId, channel, 0, baseValue(o, channel));
    }
    p = setKeyframe(p, animationId, objectId, channel, frame, channels[channel]!);
  }
  return { project: p, keyed: true };
}

/** Applies a transform/pose edit to the base object (Auto-Key off). */
export function applyBaseEdit(project: Project, sceneId: string, o: SceneObject, edit: TransformEdit): Project {
  const patch: Record<string, unknown> = {};
  for (const k of ['x', 'y', 'width', 'height', 'rotation', 'opacity', 'visible'] as const) if (edit[k] !== undefined) patch[k] = edit[k];
  if (edit.pose && o.type === 'character') {
    const pose = { ...(o.pose ?? {}), ...edit.pose } as CharacterPose;
    for (const k of POSE_KEYS) pose[k] = cleanValue(`pose.${k}` as Channel, pose[k] ?? 0);
    patch.pose = pose;
  }
  return updateObjects(project, sceneId, [o.id], patch);
}

/** Channels of an object that the animation overrides (shown as a notice on base edits). */
export function animatedChannels(a: Animation | null | undefined, objectId: string): Channel[] {
  return a ? a.tracks.filter((t) => t.objectId === objectId && t.keyframes.length > 0).map((t) => t.channel) : [];
}

/** Stable, canonical JSON of an animation (tracks in order, keys sorted). */
export function serializeAnimation(a: Animation): string {
  const sortKeys = (value: unknown): unknown =>
    Array.isArray(value)
      ? value.map(sortKeys)
      : value && typeof value === 'object'
        ? Object.fromEntries(Object.keys(value as object).sort().map((k) => [k, sortKeys((value as Record<string, unknown>)[k])]))
        : value;
  return JSON.stringify(sortKeys(a));
}
